import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Save, Loader2, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import DISCBadge, { DISC_CONFIG } from './DISCBadge';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function DISCEditor({ open, onOpenChange, contactId, contactName, currentScores, onSave }) {
    const { getAuthHeader } = useAuth();
    const [scores, setScores] = useState({ D: 50, I: 50, S: 50, C: 50 });
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [assessing, setAssessing] = useState(false);
    const [assessment, setAssessment] = useState(null);

    useEffect(() => {
        if (open && currentScores) {
            setScores(currentScores);
        } else if (open) {
            setScores({ D: 50, I: 50, S: 50, C: 50 });
        }
        setAssessment(null);
    }, [open, currentScores]);

    const primary = Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || 'S';
    const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const secondary = sorted[1]?.[1] > 40 ? `${sorted[0][0]}${sorted[1][0]}` : sorted[0][0];

    const prefs = DISC_CONFIG[primary];

    const handleAIAssess = async () => {
        setAssessing(true);
        setAssessment(null);
        try {
            const res = await axios.post(`${API}/api/contacts/${contactId}/disc/assess`, {}, { headers: getAuthHeader() });
            if (res.data.assessment) {
                setAssessment(res.data.assessment);
                setScores(res.data.assessment.scores);
                toast.success(`AI assessed DISC with ${res.data.assessment.confidence}% confidence`);
            } else {
                toast.info(res.data.message || 'Not enough data for assessment');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'AI assessment failed');
        }
        finally { setAssessing(false); }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/api/contacts/${contactId}/disc`, {
                disc_scores: scores,
                disc_notes: notes,
            }, { headers: getAuthHeader() });
            toast.success('DISC profile saved');
            if (onSave) onSave({ disc_scores: scores, disc_profile: primary, disc_secondary: secondary });
            onOpenChange(false);
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>DISC Assessment — {contactName}</DialogTitle>
                    <DialogDescription>Set personality scores to personalize AI-powered communications.</DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    {/* AI Assess Button */}
                    <div className="bg-stone-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-stone-700">AI Auto-Assess</p>
                            <p className="text-xs text-stone-500">Analyze email history to infer personality</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleAIAssess} disabled={assessing}>
                            {assessing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Analyzing...</> :
                            <><Sparkles className="w-3.5 h-3.5 mr-1" /> Assess</>}
                        </Button>
                    </div>

                    {/* AI Assessment Result */}
                    {assessment && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                            <p className="text-xs font-medium text-amber-700">
                                AI Confidence: {assessment.confidence}%
                            </p>
                            <p className="text-xs text-amber-600">{assessment.reasoning}</p>
                            {assessment.communication_style && (
                                <p className="text-xs text-amber-600 italic">{assessment.communication_style}</p>
                            )}
                        </div>
                    )}

                    {/* Sliders */}
                    <div className="space-y-4">
                        {Object.entries(DISC_CONFIG).map(([key, cfg]) => (
                            <div key={key}>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-5 h-5 rounded-full ${cfg.dot} text-white text-[10px] font-bold flex items-center justify-center`}>{key}</span>
                                        <span className="text-sm font-medium text-stone-700">{cfg.label}</span>
                                    </div>
                                    <span className="text-sm font-bold text-stone-900">{scores[key]}</span>
                                </div>
                                <input
                                    type="range"
                                    min={0} max={100} value={scores[key]}
                                    onChange={e => setScores(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                                    className="w-full h-2 bg-stone-200 rounded-full appearance-none cursor-pointer accent-stone-900"
                                />
                                <p className="text-xs text-stone-400 mt-0.5">{cfg.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Live Preview */}
                    <div className="border-t border-stone-200 pt-4">
                        <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Preview</h4>
                        <div className="flex items-center gap-3 mb-3">
                            <DISCBadge profile={primary} secondary={secondary} size="md" />
                            <span className="text-sm text-stone-600">{prefs?.desc}</span>
                        </div>
                        <DISCBadge profile={primary} secondary={secondary} scores={scores} size="lg" />
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1">Assessment Notes</label>
                        <textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            rows={2}
                            placeholder="Any observations about their communication style..."
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                        />
                    </div>

                    {/* Save */}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</> :
                            <><Save className="w-4 h-4 mr-1.5" /> Save Profile</>}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
